"use client"

import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Image from "next/image"
import { caseStudies } from "@/lib/data"
import type { CaseStudy } from "@/lib/types"
import { useLanguage } from "@/lib/i18n"

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger)
}

// Get large case studies (alphabetically ordered in data file)
const largeCases = caseStudies.filter((c) => c.tier === "large")

// Work Card Component - Compact card in grid
function WorkCard({
  caseStudy,
  index,
  isActive,
  onSelect,
}: {
  caseStudy: CaseStudy
  index: number
  isActive: boolean
  onSelect: () => void
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      onSelect()
    }
  }

  return (
    <motion.article
      className={`
        work-card relative group cursor-pointer rounded-xl overflow-hidden
        transition-all duration-300 ease-out aspect-[4/3]
        ${isActive ? "ring-2 ring-[var(--color-accent-gold)]" : ""}
      `}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-expanded={isActive}
      aria-label={`View ${caseStudy.client}: ${caseStudy.title}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: index * 0.03, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -4 }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 bg-[var(--color-bg-tertiary)]">
        <Image
          src={caseStudy.image ?? "/placeholder.svg"}
          alt={caseStudy.title ?? "Case study image"}
          fill
          sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
          className="object-cover transition-all duration-700 group-hover:scale-105"
          loading="lazy"
        />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-4">
        {/* Index Number */}
        <span className="absolute top-3 left-3 text-sm font-medium text-white/40">
          {String(index + 1).padStart(2, "0")}
        </span>

        {/* Tag */}
        {caseStudy.tag && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full mb-2 w-fit bg-white/10 backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <span className="w-1 h-1 rounded-full bg-[var(--color-accent-gold)]" />
            <span className="text-[10px] font-medium text-white/80 uppercase tracking-wider">
              {caseStudy.tag}
            </span>
          </div>
        )}

        {/* Client Name */}
        <p className="text-[10px] font-semibold tracking-wide text-[var(--color-accent-gold)] uppercase mb-1">
          {caseStudy.client}
        </p>

        {/* Title */}
        <h3
          className="text-sm md:text-base font-medium text-white leading-tight"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {caseStudy.title}
        </h3>
      </div>

      {/* Hover/Active border effect */}
      <div
        className={`absolute inset-0 rounded-xl border transition-all duration-300 pointer-events-none ${isActive ? "border-[var(--color-accent-gold)]" : "border-white/0 group-hover:border-white/20"}`}
      />
    </motion.article>
  )
}

// Expanded Detail Panel Component
function DetailPanel({
  caseStudy,
  onClose,
  t,
}: {
  caseStudy: CaseStudy
  onClose: () => void
  t: (path: string) => string
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="col-span-full"
    >
      <div className="bg-[var(--color-bg-secondary)] rounded-xl border border-[var(--color-bg-tertiary)] overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Left: Image */}
          <div className="lg:w-2/5 relative aspect-[4/3] lg:aspect-auto lg:min-h-[400px]">
            <Image
              src={caseStudy.image ?? "/placeholder.svg"}
              alt={caseStudy.title ?? "Case study detail"}
              fill
              sizes="(max-width: 1024px) 100vw, 40vw"
              className="object-cover"
            />
            {/* Close button on image */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/70 transition-all duration-300"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Right: Content */}
          <div className="lg:w-3/5 p-6 md:p-8">
            {/* Header */}
            <div className="mb-6">
              <p className="text-xs font-semibold tracking-[0.15em] text-[var(--color-accent-gold)] uppercase mb-2">
                {caseStudy.client}
              </p>
              <h3
                className="text-2xl md:text-3xl font-medium text-[var(--color-text-primary)] mb-3 leading-tight"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {caseStudy.title}
              </h3>
              {caseStudy.tagline && (
                <p className="text-base text-[var(--color-text-secondary)] leading-relaxed">
                  {caseStudy.tagline}
                </p>
              )}
            </div>

            {/* Meta */}
            <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-[var(--color-bg-tertiary)]">
              <div>
                <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider">
                  {t("work.industry")}
                </span>
                <p className="text-sm text-[var(--color-text-primary)] font-medium mt-0.5">
                  {caseStudy.industry}
                </p>
              </div>
              <div>
                <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider">
                  {t("work.services")}
                </span>
                <p className="text-sm text-[var(--color-text-primary)] font-medium mt-0.5">
                  {caseStudy.services}
                </p>
              </div>
              <div>
                <span className="text-xs text-[var(--color-text-muted)] uppercase tracking-wider">
                  {t("work.gapBridged")}
                </span>
                <p className="text-sm text-[var(--color-accent-gold)] font-medium mt-0.5">
                  {caseStudy.gap}
                </p>
              </div>
            </div>

            {/* Challenge, Approach, Impact */}
            <div className="space-y-5">
              {caseStudy.challenge && (
                <div>
                  <h4 className="text-xs font-semibold tracking-[0.1em] uppercase text-[var(--color-accent-teal)] mb-2">
                    {t("work.theChallenge")}
                  </h4>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                    {caseStudy.challenge}
                  </p>
                </div>
              )}

              {caseStudy.approach && (
                <div>
                  <h4 className="text-xs font-semibold tracking-[0.1em] uppercase text-[var(--color-accent-teal)] mb-2">
                    {t("work.theApproach")}
                  </h4>
                  <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
                    {caseStudy.approach}
                  </p>
                </div>
              )}

              {caseStudy.impact && caseStudy.impact.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold tracking-[0.1em] uppercase text-[var(--color-accent-teal)] mb-2">
                    {t("work.theImpact")}
                  </h4>
                  <ul className="space-y-1.5">
                    {caseStudy.impact.map((item: string, i: number) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]"
                      >
                        <span className="text-[var(--color-accent-gold)] mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* CTA */}
            <div className="mt-6 pt-6 border-t border-[var(--color-bg-tertiary)] flex items-center justify-between">
              {caseStudy.caseStudyUrl ? (
                <motion.a
                  href={caseStudy.caseStudyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--color-accent-gold)] text-[var(--color-bg-primary)] text-sm font-medium rounded-full hover:opacity-90 transition-opacity duration-300"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{t("work.viewFullCase")}</span>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </motion.a>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)] text-sm font-medium rounded-full cursor-not-allowed opacity-60">
                  {t("work.comingSoon")}
                </span>
              )}

              <button
                onClick={onClose}
                className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-300"
              >
                {t("work.close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

// Hook to get current column count based on viewport
function useColumnCount() {
  const [columns, setColumns] = useState(4)

  useEffect(() => {
    const updateColumns = () => {
      if (window.innerWidth < 768) {
        setColumns(2) // mobile: 2 columns
      } else if (window.innerWidth < 1024) {
        setColumns(3) // tablet: 3 columns
      } else {
        setColumns(4) // desktop: 4 columns
      }
    }

    updateColumns()
    window.addEventListener("resize", updateColumns)
    return () => window.removeEventListener("resize", updateColumns)
  }, [])

  return columns
}

export function WorkSection() {
  const { t, locale } = useLanguage()
  const sectionRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const columns = useColumnCount()

  const activeCase = activeIndex !== null ? largeCases[activeIndex] : null

  // Calculate which row the active card is in (0-indexed)
  const getRowIndex = (index: number) => Math.floor(index / columns)
  const activeRow = activeIndex !== null ? getRowIndex(activeIndex) : -1

  // Group cards by row for proper panel insertion
  const rows: number[][] = []
  largeCases.forEach((_, index) => {
    const rowIndex = getRowIndex(index)
    if (!rows[rowIndex]) rows[rowIndex] = []
    rows[rowIndex].push(index)
  })

  // GSAP ScrollTrigger animations
  useEffect(() => {
    if (!sectionRef.current) return

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".work-header",
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse",
          },
        }
      )
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section
      ref={sectionRef}
      id="work"
      data-section="work"
      className="py-20 md:py-28 px-4 md:px-8 lg:px-12 bg-[var(--color-bg-primary)] relative overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section Header */}
        <div className="work-header mb-10 md:mb-14 text-center">
          <p
            className={`text-xl md:text-2xl text-[var(--color-accent-gold)] mb-3${locale !== "zh" ? " rotate-[-1deg]" : ""}`}
            style={{ fontFamily: "var(--font-handwritten)" }}
          >
            {t("work.sectionLabel")}
          </p>

          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-medium text-[var(--color-text-primary)] leading-tight"
            style={{ fontFamily: "var(--font-display)", letterSpacing: "-0.02em" }}
          >
            {t("work.headline")}
          </h2>
        </div>

        {/* Work Grid with inline detail panels */}
        <div className="space-y-4">
          {rows.map((rowIndices, rowIndex) => (
            <div key={rowIndex}>
              {/* Cards Row */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {rowIndices.map((cardIndex) => (
                  <WorkCard
                    key={largeCases[cardIndex].id}
                    caseStudy={largeCases[cardIndex]}
                    index={cardIndex}
                    isActive={activeIndex === cardIndex}
                    onSelect={() => setActiveIndex(activeIndex === cardIndex ? null : cardIndex)}
                  />
                ))}
              </div>

              {/* Detail Panel - appears below the row if an item in this row is active */}
              <AnimatePresence>
                {activeRow === rowIndex && activeCase && (
                  <motion.div
                    key="detail-panel"
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: 1, height: "auto", marginTop: 16 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <DetailPanel
                      caseStudy={activeCase}
                      onClose={() => setActiveIndex(null)}
                      t={t}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Bottom Description */}
        <motion.p
          className="mt-10 md:mt-14 text-base text-[var(--color-text-secondary)] leading-relaxed text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {t("work.bottomDesc")}
        </motion.p>
      </div>
    </section>
  )
}
